import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap';

dayjs.locale('es');

const AddPublicacionModal = ({
    setIsOpenModal,
    isOpenModal,
    selectedDate,
    onPublicacionAdded,
    selectedCuentaId
}) => {
    const {
        config: { isDark }
    } = useContext(AppContext);
    const { token } = useToken();
    const [formData, setFormData] = useState({
        idPublicacion: null,
        descripcion: '',
        redesSociales: [],
        responsable: '',
        idCatEstadoPublicacion: 1, // ID del estado "Planeado"
        tema: '',
        idCatFormatoPublicacion: null,
        idCatLlamadaAccion: null
    });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [publicacionesDelDia, setPublicacionesDelDia] = useState([]);
    const [catalogos, setCatalogos] = useState({
        estados: [],
        formatos: [],
        llamadasAccion: [],
        usuarios: []
    });

    const redesSocialesDisponibles = [
        { id: 'facebook', nombre: 'Facebook', icon: ['fab', 'facebook'], color: '#1877F2' },
        { id: 'instagram', nombre: 'Instagram', icon: ['fab', 'instagram'], color: '#E4405F' },
        { id: 'x', nombre: 'X', icon: ['fab', 'x-twitter'], color: '#000000' },
        { id: 'tiktok', nombre: 'TikTok', icon: ['fab', 'tiktok'], color: '#000000' },
        { id: 'youtube', nombre: 'YouTube', icon: ['fab', 'youtube'], color: '#FF0000' },
        { id: 'linkedin', nombre: 'LinkedIn', icon: ['fab', 'linkedin'], color: '#0A66C2' },
        { id: 'threads', nombre: 'Threads', icon: ['fab', 'threads'], color: '#000000' }
    ];

    useEffect(() => {
        if (isOpenModal && selectedDate) {
            cargarCatalogos();
            cargarPublicacionesDelDia();
        } else {
            setFormData({
                idPublicacion: null,
                descripcion: '',
                redesSociales: [],
                responsable: '',
                idCatEstadoPublicacion: 1,
                tema: '',
                idCatFormatoPublicacion: null,
                idCatLlamadaAccion: null
            });
            setError('');
            setPublicacionesDelDia([]);
        }
    }, [isOpenModal, selectedDate, selectedCuentaId]);

    const cargarCatalogos = async () => {
        try {
            const response = await fetch('/api/calendarioeditorial/ObtenerCatalogos', {
                method: 'GET',
                headers:
                 {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                
                const usuarios = Array.from(detalles.usuarios || []);
                
                setCatalogos({
                    estados: Array.from(detalles.estados || []),
                    formatos: Array.from(detalles.formatos || []),
                    llamadasAccion: Array.from(detalles.llamadasAccion || []),
                    usuarios: usuarios
                });

                // Establecer el primer usuario como valor por defecto si hay usuarios disponibles
                if (usuarios.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        responsable: usuarios[0].vNombreCompleto
                    }));
                }
            }
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
        }
    };

    const cargarPublicacionesDelDia = async () => {
        try {
            const fechaFormateada = dayjs(selectedDate).format('YYYY-MM-DD');

            let url = `/api/calendarioeditorial/ObtenerPublicacionesPorFecha?fecha=${fechaFormateada}`;
            if (selectedCuentaId) {
                url += `&idCatCuentaComercial=${selectedCuentaId}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const publicacionesData = detalles.table1 || [];
                setPublicacionesDelDia(publicacionesData);
            } else {
                setPublicacionesDelDia([]);
            }
        } catch (error) {
            console.error('Error al cargar publicaciones del día:', error);
            setPublicacionesDelDia([]);
        }
    };

    const handleClose = () => {
        setIsOpenModal(!isOpenModal);
        setError('');
    };

    const handleChange = ({ target }) => {
        const { name, value } = target;

        if (name === 'descripcion' && value.length > 500) {
            setError('La descripci&oacute;n no puede exceder 100 caracteres');
            return;
        }

        // Convertir a número si es un campo de catálogo
        let finalValue = value;
        if (name === 'idCatEstadoPublicacion' || name === 'idCatFormatoPublicacion' || name === 'idCatLlamadaAccion') {
            finalValue = value ? parseInt(value) : null;
        }

        setFormData({ ...formData, [name]: finalValue });
        setError('');
    };

    const handleRedSocialToggle = (redSocialId) => {
        const redesActuales = [...formData.redesSociales];
        const index = redesActuales.indexOf(redSocialId);

        if (index > -1) {
            redesActuales.splice(index, 1);
        } else {
            redesActuales.push(redSocialId);
        }

        setFormData({ ...formData, redesSociales: redesActuales });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.descripcion.trim()) {
            setError('La descripci&oacute;n es requerida');
            return;
        }

        if (formData.descripcion.length > 100) {
            setError('La descripci&oacute;n no puede exceder 100 caracteres');
            return;
        }

        if (formData.redesSociales.length === 0) {
            setError('Debe seleccionar al menos una red social');
            return;
        }

        if (!formData.responsable.trim()) {
            setError('El responsable es requerido');
            return;
        }

        if (!formData.tema.trim()) {
            setError('El tema es requerido');
            return;
        }

        if (!formData.idCatFormatoPublicacion) {
            setError('El formato es requerido');
            return;
        }

        if (!formData.idCatLlamadaAccion) {
            setError('La llamada de acci&oacute;n es requerida');
            return;
        }

        if (publicacionesDelDia.length >= 3 && !formData.idPublicacion) {
            setError('No se pueden agregar m&aacute;s de 3 publicaciones por d&iacute;a');
            return;
        }

        setSaving(true);

        try {
            const requestBody = {
                idPublicacion: formData.idPublicacion || 0,
                descripcion: formData.descripcion,
                fechaPublicacion: dayjs(selectedDate).format('YYYY-MM-DD'),
                redesSociales: formData.redesSociales,
                responsable: formData.responsable,
                idCatEstadoPublicacion: formData.idCatEstadoPublicacion,
                tema: formData.tema,
                idCatFormatoPublicacion: formData.idCatFormatoPublicacion,
                idCatLlamadaAccion: formData.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/GuardarPublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                // Cerrar modal y notificar éxito
                setIsOpenModal(false);
                if (onPublicacionAdded) {
                    onPublicacionAdded(data.vMessage || 'Publicaci&oacute;n guardada exitosamente');
                }
            } else {
                setError(data.vMessage || 'Error al guardar la publicaci&oacute;n');
            }
        } catch (error) {
            console.error('Error al guardar publicación:', error);
            setError('Error al guardar la publicaci&oacute;n: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleEliminarPublicacion = async (idPublicacion) => {
        try {
            const response = await fetch('/api/calendarioeditorial/EliminarPublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idPublicacion })
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                // Recargar publicaciones del día
                cargarPublicacionesDelDia();
                if (onPublicacionAdded) {
                    onPublicacionAdded('Publicaci&oacute;n eliminada exitosamente');
                }
            } else {
                setError(data.vMessage || 'Error al eliminar la publicaci&oacute;n');
            }
        } catch (error) {
            console.error('Error al eliminar publicación:', error);
            setError('Error al eliminar la publicaci&oacute;n: ' + error.message);
        }
    };

    const getRedSocialInfo = (redSocialId) => {
        return redesSocialesDisponibles.find(red => red.id === redSocialId);
    };

    const getEstadoColor = (idEstado) => {
        const estado = catalogos.estados.find(e => e.idCatEstadoPublicacion === idEstado);
        return estado ? estado.vColorEstado : '#6c757d';
    };

    const getEstadoIcon = (idEstado) => {
        const estado = catalogos.estados.find(e => e.idCatEstadoPublicacion === idEstado);
        return estado ? estado.vIconEstado : 'circle';
    };

    const getEstadoNombre = (idEstado) => {
        const estado = catalogos.estados.find(e => e.idCatEstadoPublicacion === idEstado);
        return estado ? estado.vNombreEstado : 'Desconocido';
    };

    const getFormatoNombre = (idFormato) => {
        const formato = catalogos.formatos.find(f => f.idCatFormatoPublicacion === idFormato);
        return formato ? formato.vNombreFormato : null;
    };

    const getLlamadaAccionNombre = (idLlamada) => {
        const llamada = catalogos.llamadasAccion.find(l => l.idCatLlamadaAccion === idLlamada);
        return llamada ? llamada.vNombreLlamadaAccion : null;
    };

    return (
        <Modal
            show={isOpenModal}
            onHide={handleClose}
            contentClassName="border-0"
            dialogClassName="publicacion-modal"
            size="lg"
            centered
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header
                    closeButton
                    closeVariant={isDark ? 'white' : null}
                    className="border-bottom py-3"
                    style={{ backgroundColor: isDark ? '#1a1d20' : '#ffffff' }}
                >
                    <div className="w-100">
                        <h5 className="mb-1">Publicaciones</h5>
                        <small className="text-muted">
                            {selectedDate && dayjs(selectedDate).format('DD [de] MMMM [de] YYYY')}
                        </small>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4" style={{ backgroundColor: isDark ? '#212529' : '#f8f9fa' }}>
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                            <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                            <span dangerouslySetInnerHTML={{ __html: error }} />
                            <button type="button" className="btn-close" onClick={() => setError('')}></button>
                        </div>
                    )}

                    {/* Publicaciones existentes */}
                    {publicacionesDelDia.length > 0 && (
                        <div className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <small className="text-muted fw-semibold">
                                    {publicacionesDelDia.length} de 3 publicaciones
                                </small>
                            </div>

                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {publicacionesDelDia.map((pub, index) => {
                                    const redesSociales = pub.vRedesSociales ? JSON.parse(pub.vRedesSociales) : [];
                                    const formatoNombre = getFormatoNombre(pub.idCatFormatoPublicacion);
                                    const llamadaNombre = getLlamadaAccionNombre(pub.idCatLlamadaAccion);

                                    return (
                                        <div
                                            key={index}
                                            className="border rounded p-3 mb-2"
                                            style={{ backgroundColor: isDark ? '#2a2d31' : 'white' }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                                        <Badge
                                                            bg=""
                                                            style={{
                                                                backgroundColor: getEstadoColor(pub.idCatEstadoPublicacion),
                                                                color: 'white',
                                                                fontSize: '0.7rem',
                                                                padding: '0.25rem 0.5rem'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={getEstadoIcon(pub.idCatEstadoPublicacion)} className="me-1" />
                                                            {getEstadoNombre(pub.idCatEstadoPublicacion)}
                                                        </Badge>
                                                        {formatoNombre && (
                                                            <Badge bg="info" style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>
                                                                <FontAwesomeIcon icon="file-lines" className="me-1" />
                                                                {formatoNombre}
                                                            </Badge>
                                                        )}
                                                        {pub.vResponsable && (
                                                            <small className="text-muted">
                                                                <FontAwesomeIcon icon="user" className="me-1" />
                                                                {pub.vResponsable}
                                                            </small>
                                                        )}
                                                    </div>
                                                    {pub.vTema && (
                                                        <p className="mb-1 small fw-semibold">
                                                            <FontAwesomeIcon icon="tag" className="me-1" />
                                                            {pub.vTema}
                                                        </p>
                                                    )}
                                                    <p className="mb-2 small">{pub.vDescripcion}</p>
                                                    {llamadaNombre && (
                                                        <p className="mb-2 small text-primary">
                                                            <FontAwesomeIcon icon="bullhorn" className="me-1" />
                                                            CTA: {llamadaNombre}
                                                        </p>
                                                    )}
                                                    <div className="d-flex gap-1 flex-wrap">
                                                        {redesSociales.map((redId) => {
                                                            const redInfo = getRedSocialInfo(redId);
                                                            return redInfo ? (
                                                                <FontAwesomeIcon
                                                                    key={redId}
                                                                    icon={redInfo.icon}
                                                                    style={{ color: redInfo.color, fontSize: '1rem' }}
                                                                    title={redInfo.nombre}
                                                                />
                                                            ) : null;
                                                        })}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-danger p-1"
                                                    onClick={() => handleEliminarPublicacion(pub.idPublicacion)}
                                                >
                                                    <FontAwesomeIcon icon="trash-alt" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <hr className="my-3" />
                        </div>
                    )}

                    {publicacionesDelDia.length < 3 && (
                        <>
                            <Row className="g-3">
                                <Col xs={12}>
                                    <Form.Group controlId="tema">
                                        <Form.Label className="small fw-semibold mb-2">Tema</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="tema"
                                            value={formData.tema}
                                            onChange={handleChange}
                                            placeholder="Tema de la publicaci&oacute;n..."
                                            required
                                            style={{ fontSize: '0.9rem' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xs={12}>
                                    <Form.Group controlId="descripcion">
                                        <Form.Label className="small fw-semibold mb-2">Descripci&oacute;n</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="descripcion"
                                            value={formData.descripcion}
                                            onChange={handleChange}
                                            placeholder="Describe el contenido..."
                                            maxLength={100}
                                            required
                                            style={{ resize: 'none', fontSize: '0.9rem' }}
                                        />
                                        <div className="d-flex justify-content-end mt-1">
                                            <small className="text-muted">{formData.descripcion.length}/100</small>
                                        </div>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="formato">
                                        <Form.Label className="small fw-semibold mb-2">Formato</Form.Label>
                                        <Form.Select
                                            name="idCatFormatoPublicacion"
                                            value={formData.idCatFormatoPublicacion || ''}
                                            onChange={handleChange}
                                            required
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            <option value="">Seleccione un formato</option>
                                            {catalogos.formatos.map(formato => (
                                                <option key={formato.idCatFormatoPublicacion} value={formato.idCatFormatoPublicacion}>
                                                    {formato.vNombreFormato}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="llamadaAccion">
                                        <Form.Label className="small fw-semibold mb-2">Llamada de Acci&oacute;n (CTA)</Form.Label>
                                        <Form.Select
                                            name="idCatLlamadaAccion"
                                            value={formData.idCatLlamadaAccion || ''}
                                            onChange={handleChange}
                                            required
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            <option value="">Seleccione una opci&oacute;n</option>
                                            {catalogos.llamadasAccion.map(llamada => (
                                                <option key={llamada.idCatLlamadaAccion} value={llamada.idCatLlamadaAccion}>
                                                    {llamada.vNombreLlamadaAccion}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="responsable">
                                        <Form.Label className="small fw-semibold mb-2">Responsable</Form.Label>
                                        <Form.Select
                                            name="responsable"
                                            value={formData.responsable}
                                            onChange={handleChange}
                                            required
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            <option value="">Seleccione un responsable</option>
                                            {catalogos.usuarios.map(usuario => (
                                                <option key={usuario.idUsuario} value={usuario.vNombreCompleto}>
                                                    {usuario.vNombreCompleto}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="estado">
                                        <Form.Label className="small fw-semibold mb-2">Estado</Form.Label>
                                        <Form.Select
                                            name="idCatEstadoPublicacion"
                                            value={formData.idCatEstadoPublicacion}
                                            onChange={handleChange}
                                            required
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            {catalogos.estados.map(estado => (
                                                <option key={estado.idCatEstadoPublicacion} value={estado.idCatEstadoPublicacion}>
                                                    {estado.vNombreEstado}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold mb-2">Redes Sociales</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {redesSocialesDisponibles.map(red => {
                                                const isSelected = formData.redesSociales.includes(red.id);
                                                return (
                                                    <div
                                                        key={red.id}
                                                        onClick={() => handleRedSocialToggle(red.id)}
                                                        style={{
                                                            backgroundColor: isSelected ? '#009bdb' : 'transparent',
                                                            border: `2px solid ${isSelected ? '#009bdb' : '#e9ecef'}`,
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            minWidth: '36px',
                                                            height: '36px',
                                                            boxShadow: isSelected ? '0 2px 4px rgba(0,155,219,0.3)' : 'none',
                                                            transition: 'all 0.2s',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={red.nombre}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                            e.currentTarget.style.boxShadow = isSelected ? '0 4px 8px rgba(0,155,219,0.4)' : '0 2px 4px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                            e.currentTarget.style.boxShadow = isSelected ? '0 2px 4px rgba(0,155,219,0.3)' : 'none';
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={red.icon}
                                                            style={{
                                                                fontSize: '1rem',
                                                                color: isSelected ? 'white' : red.color
                                                            }}
                                                        />
                                                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: isSelected ? 'white' : '#6c757d' }}>
                                                            {red.nombre}
                                                        </span>
                                                        {isSelected && (
                                                            <FontAwesomeIcon
                                                                icon="check"
                                                                style={{
                                                                    fontSize: '0.8rem',
                                                                    color: 'white',
                                                                    marginLeft: '4px'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}

                    {publicacionesDelDia.length >= 3 && (
                        <div className="alert alert-warning mb-0">
                            <FontAwesomeIcon icon="info-circle" className="me-2" />
                            L&iacute;mite de 3 publicaciones alcanzado
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer
                    className="border-top p-3"
                    style={{ backgroundColor: isDark ? '#1a1d20' : '#ffffff' }}
                >
                    <Button
                        variant="light"
                        onClick={handleClose}
                        disabled={saving}
                        size="sm"
                    >
                        Cancelar
                    </Button>
                    {publicacionesDelDia.length < 3 && (
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={saving || formData.redesSociales.length === 0}
                            size="sm"
                        >
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

AddPublicacionModal.propTypes = {
    setIsOpenModal: PropTypes.func.isRequired,
    isOpenModal: PropTypes.bool.isRequired,
    selectedDate: PropTypes.instanceOf(Date),
    onPublicacionAdded: PropTypes.func,
    selectedCuentaId: PropTypes.number
};

export default AddPublicacionModal;
